import Request from '../../utilities/Request';
import moment from 'moment';
import uuid from '../../utilities/localFirst/uuid';
import { parseSlotDroppableId, parseDraggableId, parseMealIdeaDraggableId } from '../../react/components/calendar/dragTypes';
import { enqueueFoodOp, foodNowSeconds, isFoodActive, refreshFood } from './food';
import { groceryListDraftOf } from '../food/groceryListDraft';
import { mealKey } from '../food/normalize';
import { mealsInSlot, slotNoteOf, slotOf } from '../food/dayEvents';
import { hasMealIdeaInSlot, nowMinute, selectDayEvents, selectMealIdeasIndex } from '../food/selectors';

export const CLEAR_ACTIVE_GROCERY_LIST = 'CLEAR_ACTIVE_GROCERY_LIST';


export const SAVE_GROCERY_LIST_BEGIN = 'SAVE_GROCERY_LIST_BEGIN';
export const SAVE_GROCERY_LIST_SUCCESS = 'SAVE_GROCERY_LIST_SUCCESS';
export const SAVE_GROCERY_LIST_FAIL = 'SAVE_GROCERY_LIST_FAIL';


export const ADD_MEAL_IDEA_TO_GROCERY_LIST_BEGIN = 'ADD_MEAL_IDEA_TO_GROCERY_LIST_BEGIN';
export const ADD_MEAL_IDEA_TO_GROCERY_LIST_SUCCESS = 'ADD_MEAL_IDEA_TO_GROCERY_LIST_SUCCESS';
export const ADD_MEAL_IDEA_TO_GROCERY_LIST_FAIL = 'ADD_MEAL_IDEA_TO_GROCERY_LIST_FAIL';

export const REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_BEGIN = 'REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_BEGIN';
export const REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_SUCCESS = 'REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_SUCCESS';
export const REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_FAIL = 'REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_FAIL';


/** A calendar event the server has saved or deleted: its answer, which the food store takes. */
export const CALENDAR_EVENT_SAVED = 'CALENDAR_EVENT_SAVED';

export const POPULATE_VISIBLE_DATES = 'POPULATE_VISIBLE_DATES';
export const POPULATE_VISIBLE_DATES_FAIL = 'POPULATE_VISIBLE_DATES_FAIL';

export const CHANGE_ACTIVE_GROCERY_LIST = 'CHANGE_ACTIVE_GROCERY_LIST';


export const GROCERY_LIST_ITEM_REORDER = 'GROCERY_LIST_ITEM_REORDER';
/** The active list's draft (see food/groceryListDraft.js), started or replaced. */
export const GROCERY_LIST_DRAFT_KEPT = 'GROCERY_LIST_DRAFT_KEPT';


export const CHANGE_CALENDAR_VIEW_MODE = 'CHANGE_CALENDAR_VIEW_MODE';

export const SET_COPY_ON_DRAG = 'SET_COPY_ON_DRAG';
export const SET_CALENDAR_DRAGGING = 'SET_CALENDAR_DRAGGING';

export const clearActiveGroceryList = () => {
	return {
		type: CLEAR_ACTIVE_GROCERY_LIST
	};
};

export const changeCalendarViewMode = (mode) => {
	return {
		type: CHANGE_CALENDAR_VIEW_MODE,
		payload: mode
	};
};

export const populateVisibleDates = (start, end) => (dispatch, getState) => {
	if(moment.isMoment(start) && moment.isMoment(end)) {
		const { currentStartDate, currentEndDate } = getState().calendar;
		if(start.isSame(currentStartDate, 'day') && end.isSame(currentEndDate, 'day')) {
			return;
		}
		dispatch({
			type: POPULATE_VISIBLE_DATES,
			payload: {
				start,
				end
			}
		});
		return;
	}
	console.log('received', start, end);
	console.error('Unable to set visible dates');
	dispatch({
		type: POPULATE_VISIBLE_DATES_FAIL,
	});
};

/**
 * The active list's draft, merged with the food store as it is now, and kept in the calendar's state
 * when it is new. Null while the store has no active list.
 */
export const currentGroceryListDraft = () => (dispatch, getState) => {
	const held = getState().calendar.groceryListDraft;
	const draft = groceryListDraftOf(getState(), nowMinute());
	if(draft !== held) {
		dispatch({ type: GROCERY_LIST_DRAFT_KEPT, payload: draft });
	}
	return draft;
};

/** Moves a row of the active list's draft from `startIndex` to `endIndex`, and saves the list in that order. */
export const groceryListItemDrag = (groceryListId, startIndex, endIndex) => (dispatch) => {
	const groceryList = dispatch(currentGroceryListDraft());
	if(!groceryList || groceryList.id !== groceryListId) {
		return Promise.resolve(false);
	}
	dispatch({
		type: GROCERY_LIST_ITEM_REORDER,
		payload: {
			groceryListId,
			startIndex,
			endIndex
		}
	});
	// The new order is the change, even on a list with no name and no item edited. One not saved goes back.
	return dispatch(saveGroceryList(groceryList, true)).then((saved) => {
		if(!saved) {
			dispatch({
				type: GROCERY_LIST_ITEM_REORDER,
				payload: { groceryListId, startIndex: endIndex, endIndex: startIndex },
			});
		}
		return saved;
	});
};

export const changeActiveGroceryList = (groceryListId) => {
	return {
		type: CHANGE_ACTIVE_GROCERY_LIST,
		payload: groceryListId
	};
};

/** The budget the food store is showing, or null before it holds one (and in specs without it). */
const foodBudgetOf = (getState) => {
	const food = getState().food;
	return food && food.budgetId !== null && food.budgetId !== undefined ? food.budgetId : null;
};

/** 'Y-m-d' from a moment or a 'Y-m-d' string. */
const dayOf = (date) => (moment.isMoment(date) ? date.format('YYYY-MM-DD') : String(date));

/** The day `date` as the calendar shows it (food/dayEvents.js), or undefined when it has nothing on it. */
const shownDay = (getState, date) => {
	const day = dayOf(date);
	return selectDayEvents(day, day)(getState().food.entities)[day];
};

/**
 * `after_uid` for a meal put at `index` of `meals`, a slot as it is shown without that meal: the uid
 * of the meal before that place, or null for the first. Undefined, which puts it last, when the meal
 * there has no uid to name.
 */
const afterUidAt = (meals, index) => {
	const at = Math.max(0, Math.min(index, meals.length));
	return at === 0 ? null : (meals[at - 1].uid || undefined);
};

/**
 * Meal placement, moves, reorders, removal and slot notes are ops (docs/local-first-food-data.md,
 * phase 4): each is shown at once, kept in the browser until the server has it, and sent when there is
 * a connection. Each names one meal by its uid, or one slot's note, so two people planning the same
 * day change only what each touched. The thunks below resolve true once the change is kept, which is
 * at once; a change the server later refuses is undone on screen and told (tellFoodRejections).
 */

/**
 * Put a meal on `date` (a moment or 'Y-m-d') in slot `type`, as a `meal.create` op with a new uid.
 * `fields` is its `name`, `notes`, and `meal_idea_id` when it comes from a meal idea. `afterUid` puts
 * it directly after that meal of the slot, or first for null; without it, it goes last.
 *
 * An idea the slot already holds is not added again, and that resolves true as well: the slot ends up
 * with the meal, which is what was asked for. Resolves false for a meal with no name, or no budget.
 */
export const createMeal = (date, type, fields, afterUid = undefined) => (dispatch, getState) => {
	const name = String(fields.name || '').trim();
	if(!name || !isFoodActive(getState)) {
		return Promise.resolve(false);
	}
	const day = dayOf(date);
	const mealIdeaId = fields.meal_idea_id === undefined || fields.meal_idea_id === '' ? null : fields.meal_idea_id;
	if(hasMealIdeaInSlot(getState().food.entities, day, type, mealIdeaId)) {
		return Promise.resolve(true);
	}
	const payload = { uid: uuid(), date: day, type: slotOf(type), name, notes: fields.notes || '', meal_idea_id: mealIdeaId };
	if(afterUid !== undefined) {
		payload.after_uid = afterUid;
	}
	return dispatch(enqueueFoodOp('meal.create', payload)).then((op) => !!op);
};

/** Put a grocery item on `date` as a meal by its name, with no idea behind it, last in slot `type`. */
export const addItemToDate = (date, type, name) => createMeal(date, type, { name });

const PATCHABLE = ['name', 'notes', 'meal_idea_id'];

/**
 * The meal keyed `key`, when an op can name it: one with a uid. A meal without one (no answer has
 * carried one since meals had uids, but a store keyed by `id:` can still hold one) is shown but cannot
 * be changed, since the server would refuse any op naming it. Null for either, or a meal the store
 * does not have.
 */
const opMeal = (getState, key) => {
	const meal = isFoodActive(getState) ? getState().food.entities.meals[key] : null;
	return meal && meal.uid && String(meal.uid) === String(key) ? meal : null;
};

/** A field as the store compares it: notes '' when there are none, an idea id a number or null. */
const comparable = (field, value) => {
	if(field === 'notes') {
		return value || '';
	}
	if(field === 'meal_idea_id') {
		return value === null || value === undefined || value === '' ? null : Number(value);
	}
	return value;
};

/**
 * Change the meal keyed `uid`, as a `meal.patch` op: `set` any of its `name`, `notes` and
 * `meal_idea_id`, and `move` it to `{ date, type, afterUid }` (afterUid as createMeal takes it). Only
 * what differs from the meal as shown is sent, so a change made elsewhere to another of its fields
 * stands. Resolves false for a meal the store does not have, and true without an op when nothing
 * would change.
 */
export const patchMeal = (uid, { set = {}, move = null } = {}) => (dispatch, getState) => {
	const meal = opMeal(getState, uid);
	if(!meal) {
		return Promise.resolve(false);
	}
	const payload = { uid };
	const changed = {};
	PATCHABLE.filter((field) => Object.prototype.hasOwnProperty.call(set, field)).forEach((field) => {
		const value = field === 'name' ? String(set[field] || '').trim() : set[field];
		if((field !== 'name' || value) && comparable(field, value) !== comparable(field, meal[field])) {
			changed[field] = field === 'name' ? value : comparable(field, value);
		}
	});
	if(Object.keys(changed).length) {
		payload.set = changed;
	}
	if(move) {
		payload.move = { date: dayOf(move.date), type: slotOf(move.type) };
		if(move.afterUid !== undefined) {
			payload.move.after_uid = move.afterUid;
		}
	}
	if(!payload.set && !payload.move) {
		return Promise.resolve(true);
	}
	return dispatch(enqueueFoodOp('meal.patch', payload)).then((op) => !!op);
};

/** Take the meal keyed `uid` off the calendar, as a `meal.delete` op. Resolves false for one the store does not have. */
export const deleteMeal = (uid) => (dispatch, getState) => {
	if(!opMeal(getState, uid)) {
		return Promise.resolve(false);
	}
	return dispatch(enqueueFoodOp('meal.delete', { uid })).then((op) => !!op);
};

/**
 * Set the note on slot `type` of `date`, or clear it with an empty one, as a `slot_note.set` op
 * carrying the whole note, trimmed. A note unchanged sends nothing. Resolves whether it is kept.
 */
export const setSlotNote = (date, type, notes) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve(false);
	}
	const trimmed = (notes || '').trim();
	if(trimmed === slotNoteOf(shownDay(getState, date), type)) {
		return Promise.resolve(true);
	}
	return dispatch(enqueueFoodOp('slot_note.set', { date: dayOf(date), type: slotOf(type), notes: trimmed })).then((op) => !!op);
};

/**
 * Create a calendar event, or with `id` change one, from `event`'s name, description, all_day and
 * start_time and end_time as unix timestamps; with `delete` true, delete it. The endpoint sets every
 * field from what it is sent, so a deletion sends the rest as well. Online only: the day shows the
 * change once the server's answer reaches the food store (CALENDAR_EVENT_SAVED). It names the budget
 * the food store is showing, so the server refuses it (409) when that is no longer the selected one
 * rather than writing it into another budget whose answer the store would drop. Resolves whether it
 * saved.
 */
export const saveCalendarEvent = (event) => (dispatch, getState) => {
	const data = {
		id: event.id || null,
		name: event.name,
		description: event.description || '',
		all_day: !!event.all_day,
		start_time: event.start_time,
		end_time: event.end_time,
		delete: !!event.delete,
	};
	const budgetId = foodBudgetOf(getState);
	if(budgetId !== null) {
		data.budget_id = budgetId;
	}
	return Request.apiv1Post('budget/events', data)
		.then((response) => {
			dispatch({ type: CALENDAR_EVENT_SAVED, payload: { event: response.event, budgetId: response.budget_id } });
			return true;
		})
		.catch((error) => {
			console.error(error);
			return false;
		});
};

/**
 * Tick a row of the active list off onto the list for `today`, or untick it back, as a
 * `grocery.check_off` op (docs/local-first-food-data.md, phase 3): shown at once, kept in the
 * browser until the server has it, and sent when there is a connection. The day is the browser's
 * local date when tapped, since the server may be in another timezone than the shopper, and the op
 * names the list the row was on and when it was tapped, so a tick sent hours later neither lands
 * on another day nor overrides what someone has done to the row since.
 *
 * The answer carries the row's lists and its last-bought days, so nothing is fetched again.
 * Resolves with the op, or null for a row the store does not have.
 */
export const checkOffRow = (rowId, checked, today = moment()) => (dispatch, getState) => {
	const row = getState().food ? getState().food.entities.groceryRows[rowId] : null;
	if(!row) {
		return Promise.resolve(null);
	}
	const payload = { row_id: Number(rowId), checked: !!checked, from: row.list_key, at: foodNowSeconds() };
	if(checked) {
		payload.date = today.format('YYYY-MM-DD');
	}
	return dispatch(enqueueFoodOp('grocery.check_off', payload));
};

/**
 * Set how much of the grocery row `rowId` to buy and a note on it, as a `grocery.row_set` op, so it
 * works offline. `fields` holds `quantity`, `notes` or both; only those change. Resolves with the op,
 * or null when nothing would change.
 */
export const setGroceryRow = (rowId, fields) => (dispatch, getState) => {
	const row = getState().food ? getState().food.entities.groceryRows[rowId] : null;
	if(!row) {
		return Promise.resolve(null);
	}
	const payload = { row_id: Number(rowId) };
	['quantity', 'notes'].filter((field) => field in fields).forEach((field) => {
		const value = String(fields[field] || '').trim();
		if(value !== (row[field] || '')) {
			payload[field] = value;
		}
	});
	if(Object.keys(payload).length === 1) {
		return Promise.resolve(null);
	}
	return dispatch(enqueueFoodOp('grocery.row_set', payload));
};

const ADDING = [ADD_MEAL_IDEA_TO_GROCERY_LIST_BEGIN, ADD_MEAL_IDEA_TO_GROCERY_LIST_SUCCESS, ADD_MEAL_IDEA_TO_GROCERY_LIST_FAIL];
const REMOVING = [
	REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_BEGIN,
	REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_SUCCESS,
	REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_FAIL,
];

/** Posts a change to the active list, which the server answers with that list. */
const changeActiveList = ([begin, success, fail], action, data) => (dispatch) => {
	dispatch({
		type: begin,
		payload: data,
	});
	return Request.apiv1Post(action, data)
		.then((response) => {
			dispatch({
				type: success,
				payload: {
					groceryLists: [response.grocery_list],
					deletedGroceryListId: null,
					budgetId: response.budget_id,
				},
			});
		})
		.catch((error) => {
			console.error(error);
			dispatch({
				type: fail,
				payload: data,
			});
		});
};

/**
 * Put a meal idea's ingredients on the active list, or only `ingredientIds` when given; ones already
 * there from it are not added again.
 */
export const addMealIdeaToGroceryList = (mealIdeaId, ingredientIds = null) => changeActiveList(ADDING,
	'budget/add_meal_idea_to_grocery_list',
	ingredientIds ? { meal_idea_id: mealIdeaId, meal_idea_ingredient_ids: ingredientIds } : { meal_idea_id: mealIdeaId });

/** Every idea in a group, in the group's order. */
export const addMealIdeaGroupToGroceryList = (mealIdeaGroupId) => changeActiveList(ADDING,
	'budget/add_meal_idea_group_to_grocery_list', { meal_idea_group_id: mealIdeaGroupId });

/** Takes one of an idea's ingredients off the active list, leaving it in the idea and unbought. */
export const removeMealIdeaIngredientFromGroceryList = (mealIdeaId, ingredientId) => changeActiveList(REMOVING,
	'budget/remove_meal_idea_ingredient_from_grocery_list', { meal_idea_id: mealIdeaId, meal_idea_ingredient_id: ingredientId });

/**
 * Every grocery list, again: the food store's snapshot. With no copy of the budget and a failed
 * snapshot this asks for the snapshot again: the legacy budget/grocery_lists is no longer called
 * (docs/local-first-food-data.md, phase 5).
 */
export const fetchGroceryLists = () => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	return dispatch(refreshFood());
};

export const setCopyOnDrag = (copyOnDrag) => ({
	type: SET_COPY_ON_DRAG,
	payload: !!copyOnDrag,
});

/** True while anything is being dragged, so the calendar can open its Other rows as targets. */
export const setCalendarDragging = (dragging) => ({
	type: SET_CALENDAR_DRAGGING,
	payload: !!dragging,
});

/** A grocery list row by its id, from the food store. */
const findGroceryListItem = (state, id) => (state.food && state.food.entities.groceryRows[id]) || null;

/**
 * A react-beautiful-dnd drop onto a calendar meal slot, from the dashboard's onDragEnd.
 *
 * The slot's droppableId names the day and the meal type, and the draggableId names what was
 * dropped (see components/calendar/dragTypes.js). A meal moves, or is copied when "Copy Dragged
 * Meals" is ticked, and takes the slot's type; a grocery list item or a meal idea becomes a new
 * meal there, one from an idea keeping its id so the calendar can link back to it.
 *
 * Each is one op: a move, between days or within a slot, is one `meal.patch`; a copy, an item or an
 * idea is a `meal.create` with a new uid. Where in the slot it goes is the drop's index in the slot
 * as shown, without the meal being moved, sent as the uid of the meal it follows.
 *
 * A drop that would put an idea in a slot already holding it does nothing.
 */
export const dropOnMealSlot = (result) => (dispatch, getState) => {
	const { source, destination, draggableId } = result;
	const target = destination ? parseSlotDroppableId(destination.droppableId) : null;
	if(!target || !isFoodActive(getState)) {
		return Promise.resolve(false);
	}
	const { kind, id } = parseDraggableId(draggableId);
	const { copyOnDrag } = getState().calendar;
	const targetMeals = mealsInSlot(shownDay(getState, target.date), target.type);
	const at = (meals) => afterUidAt(meals, destination.index);

	switch(kind) {
		case 'meal': {
			const from = parseSlotDroppableId(source.droppableId);
			const meal = from ? mealsInSlot(shownDay(getState, from.date), from.type)[source.index] : null;
			if(!meal) {
				return Promise.resolve(false);
			}
			const sameSlot = from.date.isSame(target.date, 'day') && slotOf(from.type) === slotOf(target.type);
			if(!copyOnDrag && sameSlot && source.index === destination.index) {
				return Promise.resolve(false);
			}
			const key = mealKey(meal);
			if(hasMealIdeaInSlot(getState().food.entities, dayOf(target.date), target.type, meal.meal_idea_id, copyOnDrag ? null : key)) {
				return Promise.resolve(false);
			}
			if(copyOnDrag) {
				return dispatch(createMeal(target.date, target.type,
					{ name: meal.name, notes: meal.notes, meal_idea_id: meal.meal_idea_id }, at(targetMeals)));
			}
			const others = targetMeals.filter((other) => mealKey(other) !== key);
			return dispatch(patchMeal(key, { move: { date: target.date, type: target.type, afterUid: at(others) } }));
		}
		case 'draggableGroceryListItem': {
			const item = findGroceryListItem(getState(), id);
			if(!item || !item.name) {
				return Promise.resolve(false);
			}
			return dispatch(createMeal(target.date, target.type, { name: item.name }, at(targetMeals)));
		}
		case 'mealIdea': {
			const parsed = parseMealIdeaDraggableId(draggableId);
			const idea = parsed ? selectMealIdeasIndex(getState()).ideasById[parsed.ideaId] : null;
			if(!idea || !idea.name) {
				return Promise.resolve(false);
			}
			return dispatch(createMeal(target.date, target.type, { name: idea.name, meal_idea_id: idea.id }, at(targetMeals)));
		}
		default:
			return Promise.resolve(false);
	}
};

/** Resolves whether the list saved. `force` sends it even when nothing in it looks changed. */
export const saveGroceryList = (groceryList, force = false) => (dispatch, getState) => {
	dispatch({
		type: SAVE_GROCERY_LIST_BEGIN,
		payload: groceryList,
	});
	const data = {
		grocery_list_id: groceryList.id,
		items: [],
		name: groceryList.name,
		// The rows the draft was built from: only these are removed when left out, so a row someone
		// else added since stays. A form post drops an empty array, so none is sent blank.
		base_row_ids: groceryList.items.filter((item) => item.id).map((item) => item.id),
	};
	let hasChanges = force;
	if(groceryList.name) {
		hasChanges = true;
	}
	for(let item of groceryList.getListableItems()) {
		item.update({
			name: item.name.trim()
		});
		/** Struck items are sent too: they keep their place in the numbering, and stay bought. */
		if(item.name) {
			data.items.push({
				id: item.id,
				temporary_id: item.instanceNumber,
				name: item.name,
			});
		}
		if(item.hasChanges()) {
			hasChanges = true;
		}
	}
	if(!hasChanges) {
		console.error('no changes');
		dispatch({
			type: SAVE_GROCERY_LIST_FAIL,
			payload: groceryList,
		});
		return Promise.resolve(false);
	}
	if(!data.items.length) {
		// An empty array is dropped from a form post, and the server would leave the last item in place.
		data.items = '';
	}
	if(!data.base_row_ids.length) {
		data.base_row_ids = '';
	}
	return Request.apiv1Post('budget/grocery_list', data)
		.then((response) => {
			dispatch({
				type: SAVE_GROCERY_LIST_SUCCESS,
				payload: {
					groceryList,
					groceryListResponse: response.grocery_list,
					budgetId: response.budget_id,
				},
			});
			return true;
		})
		.catch((error) => {
			console.error(error);
			dispatch({
				type: SAVE_GROCERY_LIST_FAIL,
				payload: groceryList,
			});
			return false;
		});
};